import { gql, TypedDocumentNode } from "@apollo/client";
import { PAGE_INFO_FRAGMENT } from "@/hooks/usePagination";

export const ELIGIBLE_STUDENTS_PAGE_SIZE = 100;

export interface EligibleStudentUniversity {
  id: string;
  name: string;
}

export interface EligibleStudentNode {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  pgaId: string;
  profilePhoto?: string;
  educationLatestPgmProgram?: {
    level: string;
    levelCode: string;
  } | null;
  university?: EligibleStudentUniversity | null;
  lastAttendedPgmUniversity?: EligibleStudentUniversity | null;
}

export interface EligibleStudentsConnection {
  nodes: EligibleStudentNode[];
  pageInfo: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    startCursor: string | null;
    endCursor: string | null;
  };
  totalCount: number;
}

export interface EventUniversityRow {
  id: string;
  legacyId: string;
  name: string;
}

export interface StudentsQueryResult {
  event: {
    id: string;
    title: string;
    registrationOpenAt: string;
    registrationCloseAt: string;
    timezone: string;
    universities: EventUniversityRow[];
  } | null;
  universityStudents: EligibleStudentsConnection;
}

export interface EligibleStudentIdsQueryResult {
  universityStudents: {
    totalCount: number;
    nodes: Pick<EligibleStudentNode, "id" | "university" | "lastAttendedPgmUniversity">[];
    pageInfo: EligibleStudentsConnection["pageInfo"];
  };
}

export interface StudentsQueryVariables {
  eventSlug: string;
  universityLegacyIds: string[];
  includeUniversity: boolean;
  search?: string;
  first?: number;
  after?: string;
  last?: number;
  before?: string;
}

export const STUDENTS_QUERY: TypedDocumentNode<StudentsQueryResult, StudentsQueryVariables> = gql`
  query getManualRegistrationStudents(
    $eventSlug: String!
    $universityLegacyIds: [String!]!
    $includeUniversity: Boolean!
    $first: Int
    $after: String
    $last: Int
    $before: String
    $search: String
  ) {
    event(slug: $eventSlug) {
      id
      title
      registrationOpenAt
      registrationCloseAt
      timezone
      universities {
        id
        legacyId
        name
      }
    }
    universityStudents(
      first: $first
      after: $after
      last: $last
      before: $before
      input: { universityLegacyIds: $universityLegacyIds, eventSlug: $eventSlug, search: $search }
    ) {
      totalCount
      nodes {
        id
        firstName
        lastName
        email
        pgaId
        profilePhoto
        educationLatestPgmProgram {
          level
          levelCode
        }
        university @include(if: $includeUniversity) {
          id
          name
        }
        lastAttendedPgmUniversity @include(if: $includeUniversity) {
          id
          name
        }
      }
      ...PageInfo
    }
  }
  ${PAGE_INFO_FRAGMENT}
`;

export const GET_ALL_ELIGIBLE_STUDENT_IDS: TypedDocumentNode<EligibleStudentIdsQueryResult, StudentsQueryVariables> =
  gql`
    query GetAllEligibleStudentIds(
      $eventSlug: String!
      $universityLegacyIds: [String!]!
      $includeUniversity: Boolean!
      $search: String
      $first: Int
      $after: String
    ) {
      universityStudents(
        first: $first
        after: $after
        input: { universityLegacyIds: $universityLegacyIds, eventSlug: $eventSlug, search: $search }
      ) {
        totalCount
        nodes {
          id
          university @include(if: $includeUniversity) {
            id
            name
          }
          lastAttendedPgmUniversity @include(if: $includeUniversity) {
            id
            name
          }
        }
        ...PageInfo
      }
    }
    ${PAGE_INFO_FRAGMENT}
  `;
